import Dexie, { type Table } from 'dexie';

export interface CachedTask {
  id: string;
  workspace_id: string;
  data: Record<string, unknown>;
  cached_at: number;
}

export interface CachedComment {
  id: string;
  submission_id: string;
  data: Record<string, unknown>;
  cached_at: number;
}

export interface CachedTaskTemplate {
  id: string;
  workspace_id: string;
  data: Record<string, unknown>;
  cached_at: number;
}

export type SyncStatus = 'pending' | 'synced' | 'failed';

export interface SyncQueueItem {
  id?: number;
  action: string;
  payload: Record<string, unknown>;
  created_at: number;
  status: SyncStatus;
  /**
   * How many times this item has been attempted. Drives the retry backoff and
   * the give-up threshold in `use-background-sync.ts`. Absent on rows queued
   * before retry support existed — treat `undefined` as 0.
   */
  attempts?: number;
  /** Epoch ms before which the item should not be retried (backoff). */
  next_attempt_at?: number;
  /** Last failure reason, surfaced to the user when an item is abandoned. */
  last_error?: string;
}

// ---------------------------------------------------------------------------
// Perf Stream I — Network-first persistence support
//
// The records below are the offline mirror of read hooks migrated to
// `createPersistedQuery`. Each table is keyed on the natural ID the hook
// queries against (user id, slug, composite query key, etc.). The "data"
// column stores the full backend response so the offline read path is a
// single Dexie `.get()` and the wrapper can re-emit the exact payload the
// caller would have received online.
// ---------------------------------------------------------------------------

export interface CachedWalletBalance {
  /** Owner id keyed: for writers `writer:<userId>`, for workspaces `ws:<workspaceId>`. */
  key: string;
  data: Record<string, unknown>;
  cached_at: number;
}

export interface CachedWalletTransactions {
  /** Composite key: `<walletId>|<page>|<pageSize>|<sort>|<typesCSV>`. */
  key: string;
  wallet_id: string;
  data: Record<string, unknown>;
  cached_at: number;
}

export interface CachedProfile {
  /** User id. */
  id: string;
  data: Record<string, unknown>;
  cached_at: number;
}

export interface CachedNotificationsPage {
  /** Composite key: `<userId>|<page>|<pageSize>|<unreadOnly>`. */
  key: string;
  user_id: string;
  data: Record<string, unknown>;
  cached_at: number;
}

export interface CachedNotificationCount {
  /** User id. */
  user_id: string;
  data: Record<string, unknown>;
  cached_at: number;
}

export interface CachedNotificationPreferences {
  /** User id. */
  user_id: string;
  data: Record<string, unknown>;
  cached_at: number;
}

export interface CachedTaskList {
  /** Composite key, see `tasksListKey` / `writerTasksListKey`. */
  key: string;
  data: Record<string, unknown>;
  cached_at: number;
}

export interface CachedTaskDetail {
  /** Task id. Distinct from `cached_tasks` so the enriched detail payload
   * (joined writer / workspace / latest submission / comments) doesn't
   * collide with the bare-task records used by `cacheTask`. */
  id: string;
  data: Record<string, unknown>;
  cached_at: number;
}

export interface CachedSubmissionComments {
  /** Composite key: `<submissionId>|<flat|threaded>|<includeResolved>`. */
  key: string;
  submission_id: string;
  data: Record<string, unknown>;
  cached_at: number;
}

class OfflineDatabase extends Dexie {
  cached_tasks!: Table<CachedTask, string>;
  cached_comments!: Table<CachedComment, string>;
  sync_queue!: Table<SyncQueueItem, number>;
  cached_task_templates!: Table<CachedTaskTemplate, string>;
  cached_wallet_balances!: Table<CachedWalletBalance, string>;
  cached_wallet_transactions!: Table<CachedWalletTransactions, string>;
  cached_profiles!: Table<CachedProfile, string>;
  cached_notifications_pages!: Table<CachedNotificationsPage, string>;
  cached_notification_counts!: Table<CachedNotificationCount, string>;
  cached_notification_preferences!: Table<CachedNotificationPreferences, string>;
  cached_task_lists!: Table<CachedTaskList, string>;
  cached_task_details!: Table<CachedTaskDetail, string>;
  cached_submission_comments!: Table<CachedSubmissionComments, string>;

  constructor() {
    super('write4me-offline');
    this.version(1).stores({
      cached_tasks: 'id, workspace_id',
      cached_comments: 'id, submission_id',
      sync_queue: '++id, status',
    });
    // v2 — additive only. Existing browsers auto-upgrade on next open.
    this.version(2).stores({
      cached_tasks: 'id, workspace_id',
      cached_comments: 'id, submission_id',
      sync_queue: '++id, status',
      cached_task_templates: 'id, workspace_id',
    });
    // v3 — Stream I network-first persistence. Additive only.
    this.version(3).stores({
      cached_tasks: 'id, workspace_id',
      cached_comments: 'id, submission_id',
      sync_queue: '++id, status',
      cached_task_templates: 'id, workspace_id',
      cached_wallet_balances: 'key',
      cached_wallet_transactions: 'key, wallet_id',
      cached_profiles: 'id',
      cached_notifications_pages: 'key, user_id',
      cached_notification_counts: 'user_id',
      cached_notification_preferences: 'user_id',
      cached_task_lists: 'key',
      cached_task_details: 'id',
      cached_submission_comments: 'key, submission_id',
    });
  }
}

export const offlineDb = new OfflineDatabase();

// --- Cached Tasks ---

export async function cacheTask(
  taskId: string,
  workspaceId: string,
  data: Record<string, unknown>,
): Promise<void> {
  await offlineDb.cached_tasks.put({
    id: taskId,
    workspace_id: workspaceId,
    data,
    cached_at: Date.now(),
  });
}

export async function getCachedTasks(
  workspaceId: string,
): Promise<CachedTask[]> {
  return offlineDb.cached_tasks
    .where('workspace_id')
    .equals(workspaceId)
    .toArray();
}

export async function getCachedTask(
  taskId: string,
): Promise<CachedTask | undefined> {
  return offlineDb.cached_tasks.get(taskId);
}

// --- Sync Queue ---

export async function addToSyncQueue(
  action: string,
  payload: Record<string, unknown>,
): Promise<number> {
  return offlineDb.sync_queue.add({
    action,
    payload,
    created_at: Date.now(),
    status: 'pending',
    attempts: 0,
  });
}

/**
 * Pending items that are due for an attempt now, oldest first.
 *
 * Ordering matters: an update or delete queued after a create must not be
 * replayed before it, or the server sees a mutation for a row that doesn't
 * exist yet. `++id` is monotonic, so sorting by it preserves the order the user
 * actually performed the actions in.
 */
export async function getSyncQueueItems(
  now: number = Date.now(),
): Promise<SyncQueueItem[]> {
  const pending = await offlineDb.sync_queue
    .where('status')
    .equals('pending')
    .toArray();

  return pending
    .filter((item) => (item.next_attempt_at ?? 0) <= now)
    .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
}

/** Pending items being held back by backoff — used to decide whether to re-arm a timer. */
export async function getDeferredSyncQueueItems(
  now: number = Date.now(),
): Promise<SyncQueueItem[]> {
  const pending = await offlineDb.sync_queue
    .where('status')
    .equals('pending')
    .toArray();

  return pending.filter((item) => (item.next_attempt_at ?? 0) > now);
}

/** Items abandoned after exhausting their retries; surfaced to the user. */
export async function getFailedSyncQueueItems(): Promise<SyncQueueItem[]> {
  return offlineDb.sync_queue.where('status').equals('failed').toArray();
}

export async function markSynced(id: number): Promise<void> {
  await offlineDb.sync_queue.update(id, { status: 'synced' });
}

/**
 * Record a failed attempt and schedule the next one.
 *
 * Stays `pending` (so it will be retried) until `attempts` reaches
 * `maxAttempts`, at which point it flips to `failed` and the caller surfaces it
 * to the user rather than letting the write vanish silently.
 */
export async function markAttemptFailed(
  id: number,
  error: string,
  opts: { maxAttempts: number; backoffMs: (attempts: number) => number },
): Promise<{ attempts: number; exhausted: boolean }> {
  const item = await offlineDb.sync_queue.get(id);
  const attempts = (item?.attempts ?? 0) + 1;
  const exhausted = attempts >= opts.maxAttempts;

  await offlineDb.sync_queue.update(id, {
    attempts,
    last_error: error,
    status: exhausted ? 'failed' : 'pending',
    next_attempt_at: exhausted ? undefined : Date.now() + opts.backoffMs(attempts),
  });

  return { attempts, exhausted };
}

/** Mark an item failed outright, with no further retries (permanent rejection). */
export async function markFailed(id: number, error?: string): Promise<void> {
  await offlineDb.sync_queue.update(id, {
    status: 'failed',
    ...(error ? { last_error: error } : {}),
  });
}

/** Put an abandoned item back in the queue — the "retry" affordance in the UI. */
export async function requeueFailedItem(id: number): Promise<void> {
  await offlineDb.sync_queue.update(id, {
    status: 'pending',
    attempts: 0,
    next_attempt_at: undefined,
  });
}

export async function discardFailedItem(id: number): Promise<void> {
  await offlineDb.sync_queue.delete(id);
}

export async function clearSyncedItems(): Promise<number> {
  return offlineDb.sync_queue.where('status').equals('synced').delete();
}

// --- Cached Task Templates ---

export async function cacheTaskTemplate(
  templateId: string,
  workspaceId: string,
  data: Record<string, unknown>,
): Promise<void> {
  await offlineDb.cached_task_templates.put({
    id: templateId,
    workspace_id: workspaceId,
    data,
    cached_at: Date.now(),
  });
}

export async function getCachedTaskTemplates(
  workspaceId: string,
): Promise<CachedTaskTemplate[]> {
  return offlineDb.cached_task_templates
    .where('workspace_id')
    .equals(workspaceId)
    .toArray();
}

export async function deleteCachedTaskTemplate(
  templateId: string,
): Promise<void> {
  await offlineDb.cached_task_templates.delete(templateId);
}

export async function getCachedTaskTemplate(
  templateId: string,
): Promise<CachedTaskTemplate | undefined> {
  return offlineDb.cached_task_templates.get(templateId);
}

// ---------------------------------------------------------------------------
// Perf Stream I — cache helpers for migrated read hooks
//
// Convention:
//   - `getCached<X>(key)` returns the stored payload or `null` (never throws)
//   - `setCached<X>(key, data)` writes through a fresh payload and updates
//     `cached_at`. Fire-and-forget at the call site.
// ---------------------------------------------------------------------------

// --- Wallet Balances ---

function writerWalletKey(userId: string): string {
  return `writer:${userId}`;
}

function workspaceWalletKey(workspaceId: string): string {
  return `ws:${workspaceId}`;
}

export async function getCachedWriterWalletBalance<T>(
  userId: string,
): Promise<T | null> {
  try {
    const row = await offlineDb.cached_wallet_balances.get(
      writerWalletKey(userId),
    );
    return (row?.data as T | undefined) ?? null;
  } catch {
    return null;
  }
}

export async function setCachedWriterWalletBalance(
  userId: string,
  data: Record<string, unknown>,
): Promise<void> {
  await offlineDb.cached_wallet_balances.put({
    key: writerWalletKey(userId),
    data,
    cached_at: Date.now(),
  });
}

export async function getCachedWorkspaceWalletBalance<T>(
  workspaceId: string,
): Promise<T | null> {
  try {
    const row = await offlineDb.cached_wallet_balances.get(
      workspaceWalletKey(workspaceId),
    );
    return (row?.data as T | undefined) ?? null;
  } catch {
    return null;
  }
}

export async function setCachedWorkspaceWalletBalance(
  workspaceId: string,
  data: Record<string, unknown>,
): Promise<void> {
  await offlineDb.cached_wallet_balances.put({
    key: workspaceWalletKey(workspaceId),
    data,
    cached_at: Date.now(),
  });
}

// --- Wallet Transactions (paginated) ---

/**
 * Build a deterministic key from a wallet id + the filter shape so different
 * pages/filters cache independently. Order-stable serialisation matters
 * because the same filter object can produce different `JSON.stringify`
 * outputs depending on key insertion order.
 */
export function walletTransactionsKey(
  walletId: string,
  filters: {
    page?: number;
    pageSize?: number;
    sort?: string;
    type?: string[];
  },
): string {
  const page = filters.page ?? 1;
  const pageSize = filters.pageSize ?? 20;
  const sort = filters.sort ?? 'newest';
  const types = [...(filters.type ?? [])].sort().join(',');
  return `${walletId}|${page}|${pageSize}|${sort}|${types}`;
}

export async function getCachedWalletTransactions<T>(
  key: string,
): Promise<T | null> {
  try {
    const row = await offlineDb.cached_wallet_transactions.get(key);
    return (row?.data as T | undefined) ?? null;
  } catch {
    return null;
  }
}

export async function setCachedWalletTransactions(
  key: string,
  walletId: string,
  data: Record<string, unknown>,
): Promise<void> {
  await offlineDb.cached_wallet_transactions.put({
    key,
    wallet_id: walletId,
    data,
    cached_at: Date.now(),
  });
}

// --- Public Profiles ---

export async function getCachedPublicProfile<T>(
  userId: string,
): Promise<T | null> {
  try {
    const row = await offlineDb.cached_profiles.get(userId);
    return (row?.data as T | undefined) ?? null;
  } catch {
    return null;
  }
}

export async function setCachedPublicProfile(
  userId: string,
  data: Record<string, unknown>,
): Promise<void> {
  await offlineDb.cached_profiles.put({
    id: userId,
    data,
    cached_at: Date.now(),
  });
}

// --- Current-user (auth) profile ---
//
// The signed-in user's own `users` row, cached so the navbar / layout render
// the right identity offline. Stored in `cached_profiles` under an `auth:`
// key prefix so it never collides with the public-profile blobs written by
// `setCachedPublicProfile` (which key on the bare user id).

function authProfileKey(userId: string): string {
  return `auth:${userId}`;
}

export async function getCachedAuthProfile<T>(
  userId: string,
): Promise<T | null> {
  try {
    const row = await offlineDb.cached_profiles.get(authProfileKey(userId));
    return (row?.data as T | undefined) ?? null;
  } catch {
    return null;
  }
}

export async function setCachedAuthProfile(
  userId: string,
  data: Record<string, unknown>,
): Promise<void> {
  await offlineDb.cached_profiles.put({
    id: authProfileKey(userId),
    data,
    cached_at: Date.now(),
  });
}

// --- Current-user writer_profiles row ---
//
// The signed-in writer's own writer_profiles row (verification status, payout
// email, writing-profile fields). Keyed under a `writer-self:` prefix in
// `cached_profiles` so it never collides with the public-profile or auth-profile
// entries for the same user id.

function writerSelfProfileKey(userId: string): string {
  return `writer-self:${userId}`;
}

export async function getCachedWriterSelfProfile<T>(
  userId: string,
): Promise<T | null> {
  try {
    const row = await offlineDb.cached_profiles.get(
      writerSelfProfileKey(userId),
    );
    return (row?.data as T | undefined) ?? null;
  } catch {
    return null;
  }
}

export async function setCachedWriterSelfProfile(
  userId: string,
  data: Record<string, unknown>,
): Promise<void> {
  await offlineDb.cached_profiles.put({
    id: writerSelfProfileKey(userId),
    data,
    cached_at: Date.now(),
  });
}

// --- Notifications ---

export function notificationsPageKey(
  userId: string,
  filters: { page?: number; pageSize?: number; unreadOnly?: boolean },
): string {
  const page = filters.page ?? 1;
  const pageSize = filters.pageSize ?? 20;
  const unreadOnly = filters.unreadOnly ?? false;
  return `${userId}|${page}|${pageSize}|${unreadOnly ? '1' : '0'}`;
}

export async function getCachedNotificationsPage<T>(
  key: string,
): Promise<T | null> {
  try {
    const row = await offlineDb.cached_notifications_pages.get(key);
    return (row?.data as T | undefined) ?? null;
  } catch {
    return null;
  }
}

export async function setCachedNotificationsPage(
  key: string,
  userId: string,
  data: Record<string, unknown>,
): Promise<void> {
  await offlineDb.cached_notifications_pages.put({
    key,
    user_id: userId,
    data,
    cached_at: Date.now(),
  });
}

export async function getCachedUnreadCount(
  userId: string,
): Promise<number | null> {
  try {
    const row = await offlineDb.cached_notification_counts.get(userId);
    if (!row) return null;
    const value = (row.data as { count?: unknown }).count;
    return typeof value === 'number' ? value : null;
  } catch {
    return null;
  }
}

export async function setCachedUnreadCount(
  userId: string,
  count: number,
): Promise<void> {
  await offlineDb.cached_notification_counts.put({
    user_id: userId,
    data: { count },
    cached_at: Date.now(),
  });
}

export async function getCachedNotificationPreferences<T>(
  userId: string,
): Promise<T | null> {
  try {
    const row = await offlineDb.cached_notification_preferences.get(userId);
    return (row?.data as T | undefined) ?? null;
  } catch {
    return null;
  }
}

export async function setCachedNotificationPreferences(
  userId: string,
  data: Record<string, unknown>,
): Promise<void> {
  await offlineDb.cached_notification_preferences.put({
    user_id: userId,
    data,
    cached_at: Date.now(),
  });
}

// --- Task Lists (employer + writer, paginated/filtered) ---

/**
 * Stable composite key for a workspace task list query so different
 * filters/sorts/pages cache independently without colliding.
 */
export function workspaceTasksListKey(
  workspaceId: string,
  filters: Record<string, unknown>,
): string {
  return `ws-list|${workspaceId}|${stableStringify(filters)}`;
}

export function writerTasksListKey(
  userId: string,
  filters: Record<string, unknown>,
): string {
  return `writer-list|${userId}|${stableStringify(filters)}`;
}

export async function getCachedTaskList<T>(key: string): Promise<T | null> {
  try {
    const row = await offlineDb.cached_task_lists.get(key);
    return (row?.data as T | undefined) ?? null;
  } catch {
    return null;
  }
}

export async function setCachedTaskList(
  key: string,
  data: Record<string, unknown>,
): Promise<void> {
  await offlineDb.cached_task_lists.put({
    key,
    data,
    cached_at: Date.now(),
  });
}

// --- Task Details (enriched single task) ---

export async function getCachedTaskDetail<T>(
  taskId: string,
): Promise<T | null> {
  try {
    const row = await offlineDb.cached_task_details.get(taskId);
    return (row?.data as T | undefined) ?? null;
  } catch {
    return null;
  }
}

export async function setCachedTaskDetail(
  taskId: string,
  data: Record<string, unknown>,
): Promise<void> {
  await offlineDb.cached_task_details.put({
    id: taskId,
    data,
    cached_at: Date.now(),
  });
}

// --- Submission Comments ---

export function submissionCommentsKey(
  submissionId: string,
  variant: 'flat' | 'threaded',
  includeResolved: boolean,
): string {
  return `${submissionId}|${variant}|${includeResolved ? '1' : '0'}`;
}

export async function getCachedSubmissionComments<T>(
  key: string,
): Promise<T | null> {
  try {
    const row = await offlineDb.cached_submission_comments.get(key);
    return (row?.data as T | undefined) ?? null;
  } catch {
    return null;
  }
}

export async function setCachedSubmissionComments(
  key: string,
  submissionId: string,
  data: Record<string, unknown>,
): Promise<void> {
  await offlineDb.cached_submission_comments.put({
    key,
    submission_id: submissionId,
    data,
    cached_at: Date.now(),
  });
}

// ---------------------------------------------------------------------------
// Internal: stable JSON stringify so equivalent filter objects produce the
// same cache key regardless of key insertion order. We keep this local to
// the cache layer because the only consumers are key builders above.
// ---------------------------------------------------------------------------

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(',')}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => a.localeCompare(b));
  return `{${entries
    .map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`)
    .join(',')}}`;
}

// ---------------------------------------------------------------------------
// Sign-out purge (audit UI-05)
// ---------------------------------------------------------------------------

/**
 * The service worker cache that holds authenticated `/api/` GET responses.
 * Must track `CACHE_NAME` in `public/sw.js`.
 */
const SW_RUNTIME_CACHE = 'write4me-v3';

/**
 * Wipe every trace of the signed-out user from this device.
 *
 * On `SIGNED_OUT`, `AuthProvider` cleared only the in-memory React Query cache
 * — its own comment saying this was "so no data crosses a user boundary". But
 * the Dexie `write4me-offline` database (wallet balances, transactions,
 * profiles, notifications, task lists and details, submission comments) and the
 * service worker's cached authenticated `/api/` responses both survived. On a
 * shared machine the previous user's financial and task data stayed readable
 * after logout, and the SW's network-first fallback would happily serve their
 * cached API responses offline.
 *
 * Only `/api/` entries are evicted from the SW cache: the app shell and static
 * assets in the same cache carry no user data, and dropping them would break
 * the offline shell for the next session.
 *
 * Best-effort throughout — a storage failure must never trap someone in a
 * signed-in-looking UI.
 */
export async function purgeLocalUserData(): Promise<void> {
  try {
    await offlineDb.delete();
    // Recreate immediately so the next session doesn't race an unopened DB.
    await offlineDb.open();
  } catch {
    // Blocked by another tab holding the connection, or storage unavailable.
  }

  try {
    if (typeof caches === 'undefined') return;
    const cache = await caches.open(SW_RUNTIME_CACHE);
    const requests = await cache.keys();
    await Promise.all(
      requests
        .filter((request) => new URL(request.url).pathname.startsWith('/api/'))
        .map((request) => cache.delete(request)),
    );
  } catch {
    // Cache Storage unavailable (private mode, unsupported browser).
  }
}
